import { useState, useCallback, useEffect } from 'react';
import { useAppointmentActions } from './useAppointmentActions';
import { 
  saveTimeBlocking, 
  getAllTimeBlockings, 
  deleteTimeBlocking, 
  updateTimeBlocking,
  checkBlockingOverlap 
} from '../firebase/appointments';
import { useToast } from '../components/ui/ToastManager';
import { format, isBefore, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatDateBR, convertDateBRToISO } from '../utils/dateUtils';

/**
 * Hook personalizado para gerenciar bloqueio de horários
 * 
 * @param {Date} initialDate - Data inicial para o bloqueio
 * @returns {Object} - Funções e estados para gerenciar bloqueios
 */
export const useTimeBlocking = ({ date, professionals, salonId }) => {
  // Obter a data atual no formato ISO (YYYY-MM-DD)
  const getCurrentDate = () => {
    const today = new Date();
    return format(today, 'yyyy-MM-dd');
  };

  const { showToast } = useToast();
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockingData, setBlockingData] = useState({
    date: date || getCurrentDate(),
    startTime: '09:00',
    endTime: '10:00',
    title: '',
    resourceId: ''
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('new'); // 'new' ou 'list'
  const [allBlockings, setAllBlockings] = useState([]);
  const [loadingBlockings, setLoadingBlockings] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingBlockingId, setEditingBlockingId] = useState(null);
  const [validationError, setValidationError] = useState('');

  const { calculateDuration } = useAppointmentActions();

  // Validar se o horário está no passado
  useEffect(() => {
    if (blockingData.date && blockingData.startTime) {
      const blockingDateTime = new Date(`${blockingData.date}T${blockingData.startTime}`);
      const now = new Date();
      
      if (blockingDateTime < now) {
        setValidationError('Não é possível bloquear horários no passado');
      } else {
        setValidationError('');
      }
    }
  }, [blockingData.date, blockingData.startTime]);

  // Carregar lista de bloqueios quando o modal é aberto
  useEffect(() => {
    if (blockModalOpen && salonId) {
      loadBlockings();
    }
  }, [blockModalOpen, salonId]);

  // Função para carregar todos os bloqueios
  const loadBlockings = async () => {
    if (!salonId) return;
    
    try {
      setLoadingBlockings(true);
      const blockings = await getAllTimeBlockings(salonId);
      setAllBlockings(blockings);
    } catch (error) {
      console.error('Erro ao carregar bloqueios:', error);
      showToast('Erro ao carregar bloqueios', 'error');
    } finally {
      setLoadingBlockings(false);
    }
  };
  
  /**
   * Abre o modal de bloqueio com a data atual
   * @param {Date} date - Data para bloqueio
   */
  const openBlockingModal = useCallback((tabName = 'new') => {
    // Obter horário atual com minutos exatos
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    // Formatação do horário de início (HH:MM)
    const startTime = `${currentHour.toString().padStart(2, '0')}:${currentMinutes.toString().padStart(2, '0')}`;
    
    // Calcular horário de fim (início + 1 hora)
    const endDate = new Date(now);
    endDate.setHours(currentHour + 1);
    const endTime = `${endDate.getHours().toString().padStart(2, '0')}:${currentMinutes.toString().padStart(2, '0')}`;
    
    // Configurar dados iniciais de bloqueio
    setBlockingData({
      date: date || getCurrentDate(),
      startTime,
      endTime,
      title: '',
      resourceId: ''
    });
    
    // Configurar estado do modal
    setEditMode(false);
    setEditingBlockingId(null);
    setActiveTab(tabName);
    
    // Abrir o modal
    setBlockModalOpen(true);
  }, [date]);

  /**
   * Prepara o modal para editar um bloqueio existente
   * @param {Object} blocking - Bloqueio a ser editado
   */
  const editBlocking = (blocking) => {
    setBlockingData({
      date: blocking.data,
      startTime: blocking.horarioInicio,
      endTime: blocking.horarioFim,
      title: blocking.titulo,
      resourceId: blocking.profissionalId || ''
    });
    
    setEditMode(true);
    setEditingBlockingId(blocking.id);
    setActiveTab('new');
  };

  /**
   * Exclui um bloqueio de horário
   * @param {string} blockingId - ID do bloqueio a ser excluído
   */
  const removeBlocking = async (blockingId) => {
    if (!salonId || !blockingId) {
      showToast('Dados incompletos', 'error');
      return;
    }
    
    try {
      setLoading(true);
      const success = await deleteTimeBlocking(salonId, blockingId);
      
      if (success) {
        showToast('Bloqueio excluído com sucesso', 'success');
        await loadBlockings(); // Recarregar a lista após excluir
      } else {
        showToast('Erro ao excluir bloqueio', 'error');
      }
    } catch (error) {
      console.error('Erro ao excluir bloqueio:', error);
      showToast(`Erro ao excluir bloqueio: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Aplica o bloqueio de horário
   * @param {string} salonIdParam - ID do salão (pode ser passado diretamente na chamada ou usar o valor do hook)
   * @returns {Promise<Object|null>} - Dados do bloqueio criado ou null em caso de erro
   */
  const applyTimeBlocking = async (salonIdParam) => {
    try {
      setLoading(true);
      
      // Usar o salonId passado como parâmetro ou o do hook
      const activeSalonId = salonIdParam || salonId;
      
      console.log("applyTimeBlocking - dados:", blockingData);
      console.log("applyTimeBlocking - salonId:", activeSalonId);
      
      // Validar campos obrigatórios
      if (!blockingData.date) {
        console.log("Erro: Data é obrigatória");
        showToast('Data é obrigatória', 'error');
        return null;
      }
      
      if (!blockingData.startTime || !blockingData.endTime) {
        console.log("Erro: Horário de início e fim são obrigatórios");
        showToast('Horário de início e fim são obrigatórios', 'error');
        return null;
      }
      
      if (!blockingData.title) {
        console.log("Erro: Motivo do bloqueio é obrigatório");
        showToast('Motivo do bloqueio é obrigatório', 'error');
        return null;
      }
      
      // Verificar se horário de início é anterior ao horário de fim
      const start = blockingData.startTime.split(':').map(Number);
      const end = blockingData.endTime.split(':').map(Number);
      
      const startMinutes = start[0] * 60 + start[1];
      const endMinutes = end[0] * 60 + end[1];
      
      if (startMinutes >= endMinutes) {
        console.log("Erro: Horário de início deve ser anterior ao horário de fim");
        showToast('Horário de início deve ser anterior ao horário de fim', 'error');
        return null;
      }
      
      // Garantir que a data esteja no formato ISO
      let formattedDate = blockingData.date;
      
      // Se a data contiver barras, está no formato brasileiro e precisa ser convertida
      if (typeof formattedDate === 'string' && formattedDate.includes('/')) {
        formattedDate = convertDateBRToISO(formattedDate);
      } 
      // Se for objeto Date, converter para string ISO
      else if (formattedDate instanceof Date) {
        formattedDate = format(formattedDate, 'yyyy-MM-dd');
      }
      
      console.log("Data formatada:", formattedDate);
      
      // Verificar se a data é no passado
      const blockingDate = new Date(formattedDate);
      const today = startOfDay(new Date());
      
      if (isBefore(blockingDate, today)) {
        console.log("Erro: Não é possível bloquear horários no passado");
        showToast('Não é possível bloquear horários no passado', 'error');
        return null;
      }
      
      // Preparar dados para verificação de sobreposição
      const dataToCheck = {
        ...blockingData,
        date: formattedDate
      };
      
      // Verificar sobreposição (exceto para o mesmo bloqueio em caso de edição)
      try {
        console.log("Verificando sobreposição...");
        const { hasOverlap, overlappingBlockings } = await checkBlockingOverlap(
          activeSalonId, 
          dataToCheck, 
          editMode ? editingBlockingId : null
        );
        
        if (hasOverlap) {
          const blockInfo = overlappingBlockings[0];
          console.log("Erro: Sobreposição detectada com:", blockInfo);
          showToast(`Existe sobreposição com o bloqueio "${blockInfo.titulo}" (${blockInfo.horarioInicio} - ${blockInfo.horarioFim})`, 'error');
          return null;
        }
      } catch (overlapError) {
        console.error("Erro ao verificar sobreposição:", overlapError);
      }
      
      let savedBlocking;
      
      try {
        if (editMode && editingBlockingId) {
          // Atualizar bloqueio existente
          console.log("Atualizando bloqueio existente:", editingBlockingId);
          savedBlocking = await updateTimeBlocking(activeSalonId, editingBlockingId, {
            ...blockingData,
            date: formattedDate
          });
          console.log("Bloqueio atualizado com sucesso:", savedBlocking);
          showToast('Bloqueio atualizado com sucesso', 'success');
        } else {
          // Salvar novo bloqueio
          console.log("Salvando novo bloqueio");
          savedBlocking = await saveTimeBlocking(activeSalonId, {
            ...blockingData,
            date: formattedDate
          });
          console.log("Bloqueio salvo com sucesso:", savedBlocking);
          showToast('Horário bloqueado com sucesso', 'success');
        }
      } catch (saveError) {
        console.error("Erro ao salvar/atualizar bloqueio:", saveError);
        throw saveError;
      }
      
      // Fechar o modal
      // setBlockModalOpen(false);
      
      // Limpar estado de edição
      setEditMode(false);
      setEditingBlockingId(null);
      
      // Recarregar lista de bloqueios
      await loadBlockings();
      
      // Mudar para a aba de lista após um bloqueio bem-sucedido
      setActiveTab('list');
      
      return savedBlocking;
    } catch (error) {
      console.error('Erro ao bloquear horário:', error);
      showToast(`Erro ao bloquear horário: ${error.message}`, 'error');
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Atualiza um campo específico do objeto blockingData
   * @param {string} field - Nome do campo a ser atualizado
   * @param {any} value - Novo valor para o campo
   */
  const updateBlockingField = (field, value) => {
    setBlockingData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Efeito para atualizar o validationError no blockingData
  useEffect(() => {
    setBlockingData(prev => ({
      ...prev,
      validationError: validationError
    }));
  }, [validationError]);
  
  // Alterar para a aba especificada
  const switchTab = (tab) => {
    setActiveTab(tab);
    
    // Se mudar para a aba de lista, resetar dados de edição
    if (tab === 'list') {
      setEditMode(false);
      setEditingBlockingId(null);
    }
  };
  
  return {
    blockModalOpen,
    blockingData,
    loading,
    loadingBlockings,
    activeTab,
    allBlockings,
    editMode,
    editingBlockingId,
    openBlockingModal,
    setBlockModalOpen,
    applyTimeBlocking,
    updateBlockingField,
    switchTab,
    editBlocking,
    removeBlocking,
    loadBlockings,
    validationError
  };
};

export default useTimeBlocking; 